import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Points } from './Points';
import { useGetNotificationSettingsQuery } from 'store/services/message/messageApi';
import { playPointsSound } from 'utils/audioManager';
import { RootState } from 'store';
import './AnimatedPoints.css';

interface ProfileAnimatedPointsProps {
  points?: number;
  isLoading?: boolean;
  type?: 'default' | 'big' | 'small';
  playSound?: boolean;
}

const getAnimationDuration = (change: number): number => {
  const absChange = Math.abs(change);
  if (absChange >= 1000) return 1200;
  if (absChange < 100) return 500;
  return 800;
};

const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3);

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

/**
 * Prop-driven animated Points component for profile pages.
 * Animates when the points prop changes (e.g., after cache invalidation).
 * Unlike AnimatedPoints which is driven by the Redux animation queue,
 * this component detects prop changes directly.
 */
export const ProfileAnimatedPoints = ({
  points = 0,
  isLoading = false,
  type = 'default',
  playSound = true,
}: ProfileAnimatedPointsProps) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: notificationSettings } = useGetNotificationSettingsQuery(undefined, { skip: !user });
  const soundEnabled = notificationSettings?.soundEnabled ?? true;

  const [displayValue, setDisplayValue] = useState(points);
  const [differential, setDifferential] = useState<number | null>(null);
  const [glowClass, setGlowClass] = useState('');

  const animationRef = useRef<number | null>(null);
  const previousPointsRef = useRef(points);
  const isInitialMount = useRef(true);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Skip animation on initial mount / when loading
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousPointsRef.current = points;
      setDisplayValue(points);
      return;
    }

    const change = points - previousPointsRef.current;

    // No change — just sync
    if (change === 0) {
      setDisplayValue(points);
      return;
    }

    // Cancel any running animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    const startValue = previousPointsRef.current;
    const duration = getAnimationDuration(change);
    const startTime = performance.now();

    // Show differential and glow
    setDifferential(change);
    setGlowClass('points-glow-positive');

    // Play coin sound (only for own profile)
    if (playSound) playPointsSound(change, soundEnabled);

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOut(progress);

      const currentValue = Math.round(
        startValue + (points - startValue) * easedProgress
      );
      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        animationRef.current = null;
        previousPointsRef.current = points;

        setTimeout(() => {
          setDifferential(null);
          setGlowClass('');
        }, 500);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
    previousPointsRef.current = points;

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [points, soundEnabled, playSound]);

  return (
    <div className={`animated-points-wrapper ${glowClass}`}>
      <Points points={displayValue} isLoading={isLoading} type={type} />
      {differential !== null && (
        <span className="points-differential">
          {differential > 0 ? '+' : ''}
          {formatNumber(differential)}
        </span>
      )}
    </div>
  );
};

export default ProfileAnimatedPoints;
